import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { supabase } from '../../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = ['Profile', 'Athletes', 'Call Notes', 'Employment'];

const CONTRACT_TYPES = [
  { value: 'w2',        label: 'W-2 Employee' },
  { value: '1099',      label: '1099 Contractor' },
  { value: 'volunteer', label: 'Volunteer' },
];

const PAY_TYPES = [
  { value: 'salary',      label: 'Annual Salary' },
  { value: 'hourly',      label: 'Hourly Rate' },
  { value: 'per_athlete', label: 'Per-Athlete (monthly)' },
  { value: 'volunteer',   label: 'Volunteer (unpaid)' },
];

const PAY_FREQUENCIES = [
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly',  label: 'Monthly' },
];

const BG_STATUSES = [
  { value: 'clear',   label: 'Clear',   cls: 'admin-plan-badge--done' },
  { value: 'pending', label: 'Pending', cls: 'admin-plan-badge--progress' },
  { value: 'expired', label: 'Expired', cls: 'admin-plan-badge--missing' },
];

const PLAN_STATUS = {
  sent:        { label: 'Live',        cls: 'admin-plan-badge--done' },
  in_progress: { label: 'In Progress', cls: 'admin-plan-badge--progress' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function centsToDisplay(cents, payType) {
  if (cents == null) return '';
  const dollars = (cents / 100).toFixed(2);
  if (payType === 'salary')      return `$${Number(dollars).toLocaleString()}/yr`;
  if (payType === 'hourly')      return `$${dollars}/hr`;
  if (payType === 'per_athlete') return `$${dollars}/athlete/mo`;
  return '';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const EMPTY_EMP = {
  contract_type: 'w2', pay_type: 'salary', rate_dollars: '',
  payment_frequency: 'monthly', start_date: '', certifications: '',
  cert_expiry: '', background_check_status: 'pending', background_check_date: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  payment_notes: '', admin_notes: '', w2_issued_years: [],
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function CoachProfileAdmin() {
  const { id }    = useParams();
  const navigate  = useNavigate();

  // Remember last viewed coach for tab navigation
  useEffect(() => {
    if (id) sessionStorage.setItem('apex_last_coach', id);
  }, [id]);

  const [activeTab, setActiveTab] = useState('Profile');
  const [loading, setLoading]     = useState(true);

  const [coach, setCoach]               = useState(null);
  const [coachProfile, setCoachProfile] = useState(null);
  const [athletes, setAthletes]         = useState([]);
  const [callNotes, setCallNotes]       = useState([]);
  const [notesLoaded, setNotesLoaded]   = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);

  const [editBio, setEditBio]                 = useState('');
  const [editCredentials, setEditCredentials] = useState('');
  const [profileSaving, setProfileSaving]     = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [emp, setEmp]         = useState(EMPTY_EMP);
  const [empSaving, setEmpSaving] = useState(false);
  const [w2Year, setW2Year]   = useState(new Date().getFullYear() - 1);
  const [archiving, setArchiving] = useState(false);

  // ─── Load ────────────────────────────────────────────────────────────────

  const loadCoach = useCallback(async () => {
    setLoading(true);

    const [
      { data: profileRow },
      { data: cpRow },
      { data: empRow },
    ] = await Promise.all([
      supabase.from('profiles').select('id, full_name, email').eq('id', id).maybeSingle(),
      supabase.from('coach_profiles').select('*').eq('id', id).maybeSingle(),
      supabase.from('coach_employment').select('*').eq('id', id).maybeSingle(),
    ]);

    if (!profileRow) { navigate('/admin/coaches'); return; }

    setCoach(profileRow);
    setCoachProfile(cpRow ?? null);
    setEditBio(cpRow?.bio ?? '');
    setEditCredentials(cpRow?.credentials ?? '');

    if (empRow) {
      setEmp({
        contract_type:           empRow.contract_type ?? 'w2',
        pay_type:                empRow.pay_type ?? 'salary',
        rate_dollars:            empRow.rate_cents != null ? String(empRow.rate_cents / 100) : '',
        payment_frequency:       empRow.payment_frequency ?? 'monthly',
        start_date:              empRow.start_date ?? '',
        certifications:          empRow.certifications ?? '',
        cert_expiry:             empRow.cert_expiry ?? '',
        background_check_status: empRow.background_check_status ?? 'pending',
        background_check_date:   empRow.background_check_date ?? '',
        emergency_contact_name:  empRow.emergency_contact_name ?? '',
        emergency_contact_phone: empRow.emergency_contact_phone ?? '',
        payment_notes:           empRow.payment_notes ?? '',
        admin_notes:             empRow.admin_notes ?? '',
        w2_issued_years:         empRow.w2_issued_years ?? [],
      });
    }

    const { data: aps } = await supabase
      .from('athlete_profiles').select('id').eq('coach_id', id);

    if (aps?.length) {
      const athleteIds = aps.map(a => a.id);
      const [{ data: names }, { data: assignments }] = await Promise.all([
        supabase.from('profiles').select('id, full_name, email').in('id', athleteIds).order('full_name'),
        supabase.from('athlete_plan_assignments').select('athlete_id, plans(status)').in('athlete_id', athleteIds),
      ]);
      const statusMap = {};
      (assignments ?? []).forEach(a => {
        if (!statusMap[a.athlete_id]) statusMap[a.athlete_id] = a.plans?.status ?? 'in_progress';
      });
      setAthletes((names ?? []).map(n => ({ ...n, planStatus: statusMap[n.id] ?? null })));
    } else {
      setAthletes([]);
    }

    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { loadCoach(); }, [loadCoach]);

  useEffect(() => {
    if (activeTab !== 'Call Notes' || notesLoaded) return;
    loadCallNotes();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadCallNotes() {
    setNotesLoading(true);
    const { data: notes } = await supabase
      .from('coaching_call_notes')
      .select('id, athlete_id, call_date, notes')
      .eq('coach_id', id)
      .order('call_date', { ascending: false });

    if (!notes?.length) { setCallNotes([]); setNotesLoading(false); setNotesLoaded(true); return; }

    const athleteIds = [...new Set(notes.map(n => n.athlete_id))];
    const { data: nameRows } = await supabase.from('profiles').select('id, full_name').in('id', athleteIds);
    const nameMap = Object.fromEntries((nameRows ?? []).map(r => [r.id, r.full_name]));
    setCallNotes(notes.map(n => ({ ...n, athlete_name: nameMap[n.athlete_id] ?? 'Unknown Athlete' })));
    setNotesLoading(false);
    setNotesLoaded(true);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    const ext = file.name.split('.').pop();
    const path = `coach_${id}.${ext}`;
    await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const url = `${urlData.publicUrl}?t=${Date.now()}`;
    await supabase.from('coach_profiles').upsert({ id, avatar_url: url }, { onConflict: 'id' });
    setCoachProfile(prev => ({ ...(prev ?? { id }), avatar_url: url }));
    setAvatarUploading(false);
  }

  async function handleToggleArchive() {
    const newValue = !coachProfile?.archived;
    setArchiving(true);
    await supabase.from('coach_profiles').upsert({ id, archived: newValue }, { onConflict: 'id' });
    setCoachProfile(prev => ({ ...(prev ?? { id }), archived: newValue }));
    setArchiving(false);
  }

  async function handleSaveProfile() {
    setProfileSaving(true);
    const { error } = await supabase.from('coach_profiles').upsert(
      { id, bio: editBio, credentials: editCredentials },
      { onConflict: 'id' }
    );
    if (error) { console.error('[save profile] error:', error.message); setProfileSaving(false); return; }
    setCoachProfile(prev => ({ ...(prev ?? { id }), bio: editBio, credentials: editCredentials }));
    setProfileSaving(false);
  }

  async function handleSaveEmployment() {
    setEmpSaving(true);
    const rateCents = emp.rate_dollars !== '' ? Math.round(parseFloat(emp.rate_dollars) * 100) : null;
    const { error } = await supabase.from('coach_employment').upsert({
      id,
      contract_type:           emp.contract_type,
      pay_type:                emp.pay_type,
      rate_cents:              rateCents,
      payment_frequency:       emp.payment_frequency,
      start_date:              emp.start_date || null,
      certifications:          emp.certifications || null,
      cert_expiry:             emp.cert_expiry || null,
      background_check_status: emp.background_check_status,
      background_check_date:   emp.background_check_date || null,
      emergency_contact_name:  emp.emergency_contact_name || null,
      emergency_contact_phone: emp.emergency_contact_phone || null,
      payment_notes:           emp.payment_notes || null,
      admin_notes:             emp.admin_notes || null,
      w2_issued_years:         emp.w2_issued_years,
      updated_at:              new Date().toISOString(),
    }, { onConflict: 'id' });
    if (error) console.error('[save employment] error:', error.message);
    setEmpSaving(false);
  }

  async function handleIssueW2() {
    const year = Number(w2Year);
    if (emp.w2_issued_years.includes(year)) return;
    const updated = [...emp.w2_issued_years, year].sort((a, b) => b - a);
    setEmp(prev => ({ ...prev, w2_issued_years: updated }));
    await supabase.from('coach_employment')
      .upsert({ id, w2_issued_years: updated, updated_at: new Date().toISOString() }, { onConflict: 'id' });
  }

  async function handleRemoveW2Year(year) {
    const updated = emp.w2_issued_years.filter(y => y !== year);
    setEmp(prev => ({ ...prev, w2_issued_years: updated }));
    await supabase.from('coach_employment')
      .upsert({ id, w2_issued_years: updated, updated_at: new Date().toISOString() }, { onConflict: 'id' });
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 300 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  const bgInfo = BG_STATUSES.find(s => s.value === (emp.background_check_status ?? 'pending')) ?? BG_STATUSES[1];
  const currentYear = new Date().getFullYear();
  const w2YearOptions = [];
  for (let y = currentYear; y >= currentYear - 10; y--) w2YearOptions.push(y);

  const certExpired = emp.cert_expiry && new Date(emp.cert_expiry) < new Date();

  return (
    <Layout>
      {/* Back */}
      <div style={{ marginBottom: 16 }}>
        <Link to="/admin/coaches" style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600, textDecoration: 'none' }}>
          ← Coach Directory
        </Link>
      </div>

      {/* ── Profile Header Card ─────────────────────────────────────────────── */}
      <div className="section-card" style={{ marginTop: 0, marginBottom: 24 }}>
        <div style={{ padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {/* Avatar (click to upload) */}
          <label style={{ cursor: 'pointer', flexShrink: 0 }} title="Click to upload photo">
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} disabled={avatarUploading} />
            <div className="profile-avatar">
              {coachProfile?.avatar_url
                ? <img src={coachProfile.avatar_url} alt={coach?.full_name} />
                : initials(coach?.full_name)}
              <div className="profile-avatar-overlay">{avatarUploading ? '…' : 'Upload'}</div>
            </div>
          </label>

          {/* Name & email */}
          <div style={{ flex: 1, minWidth: 180 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--navy)', letterSpacing: '-0.02em' }}>
              {coach?.full_name}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--gray-400)' }}>{coach?.email}</p>
            {coachProfile?.credentials && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--gray-500)' }}>{coachProfile.credentials}</p>
            )}
          </div>

          {/* Quick badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {emp.contract_type && (
              <span className="badge badge-navy">
                {CONTRACT_TYPES.find(c => c.value === emp.contract_type)?.label ?? emp.contract_type}
              </span>
            )}
            <span className={`admin-plan-badge ${bgInfo.cls}`}>
              BG: {bgInfo.label}
            </span>
            <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--navy)' }}>
              {athletes.length} Athlete{athletes.length !== 1 ? 's' : ''}
            </span>
            {certExpired && (
              <span className="admin-plan-badge admin-plan-badge--missing">Certs Expired</span>
            )}
            {coachProfile?.archived && (
              <span className="admin-plan-badge admin-plan-badge--missing">Archived</span>
            )}
            <button
              className="btn-xs btn-xs-ghost"
              style={coachProfile?.archived ? {} : { color: 'var(--gray-400)' }}
              onClick={handleToggleArchive}
              disabled={archiving}
            >
              {archiving ? '…' : coachProfile?.archived ? 'Restore' : 'Archive'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="sub-nav">
        {TABS.map(tab => (
          <button
            key={tab}
            className={`sub-nav-link${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Profile Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'Profile' && (
        <div className="section-card" style={{ marginTop: 0 }}>
          <div className="section-card-header">
            <span className="section-card-title">Coach Profile</span>
          </div>
          <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 560 }}>
            <div className="form-group">
              <label className="form-label">Credentials <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>(shown to athletes)</span></label>
              <input
                type="text"
                className="form-input"
                value={editCredentials}
                onChange={e => setEditCredentials(e.target.value)}
                placeholder="e.g. USATF Level 2, SafeSport Certified, 15 yrs coaching"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Bio <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>(shown to athletes)</span></label>
              <textarea
                className="form-input"
                value={editBio}
                onChange={e => setEditBio(e.target.value)}
                rows={5}
                placeholder="Coach bio shown on athlete dashboards…"
                style={{ resize: 'vertical' }}
              />
            </div>
            <div>
              <button className="btn btn-primary" onClick={handleSaveProfile} disabled={profileSaving} style={{ width: 'auto' }}>
                {profileSaving ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Athletes Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'Athletes' && (
        <div className="section-card" style={{ marginTop: 0 }}>
          <div className="section-card-header">
            <span className="section-card-title">Assigned Athletes ({athletes.length})</span>
          </div>
          {athletes.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No athletes assigned</p>
              <p className="empty-state-sub">Athletes are assigned to this coach from the Admin dashboard.</p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>Email</th>
                  <th>Plan Status</th>
                </tr>
              </thead>
              <tbody>
                {athletes.map(a => {
                  const ps = a.planStatus ? (PLAN_STATUS[a.planStatus] ?? null) : null;
                  return (
                    <tr
                      key={a.id}
                      className="tr-clickable"
                      onClick={() => navigate(`/coach/athletes/${a.id}`)}
                    >
                      <td className="admin-table-name">{a.full_name}</td>
                      <td style={{ color: 'var(--gray-500)' }}>{a.email}</td>
                      <td>
                        {ps
                          ? <span className={`admin-plan-badge ${ps.cls}`}>{ps.label}</span>
                          : <span className="admin-plan-badge admin-plan-badge--missing">Not Started</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Call Notes Tab ───────────────────────────────────────────────────── */}
      {activeTab === 'Call Notes' && (
        <div className="section-card" style={{ marginTop: 0 }}>
          <div className="section-card-header">
            <span className="section-card-title">Call Notes ({callNotes.length})</span>
          </div>
          {notesLoading ? (
            <div className="loading-screen" style={{ minHeight: 180 }}>
              <div className="loading-dot-row">
                <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
              </div>
            </div>
          ) : callNotes.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No call notes yet</p>
              <p className="empty-state-sub">Notes appear here as the coach logs calls with athletes.</p>
            </div>
          ) : (
            <div>
              {callNotes.map(note => (
                <div key={note.id} style={{ padding: '18px 28px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{note.athlete_name}</span>
                    <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      {new Date(note.call_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-600)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {note.notes || <em style={{ color: 'var(--gray-300)' }}>No notes recorded</em>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Employment Tab ───────────────────────────────────────────────────── */}
      {activeTab === 'Employment' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Contract & Pay */}
          <div className="section-card" style={{ marginTop: 0 }}>
            <div className="section-card-header">
              <span className="section-card-title">Contract & Compensation</span>
            </div>
            <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="plan-controls" style={{ flexWrap: 'wrap' }}>
                <div className="form-group">
                  <label className="form-label">Contract Type</label>
                  <select className="form-input form-select" value={emp.contract_type} onChange={e => setEmp(p => ({ ...p, contract_type: e.target.value }))}>
                    {CONTRACT_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Pay Type</label>
                  <select className="form-input form-select" value={emp.pay_type} onChange={e => setEmp(p => ({ ...p, pay_type: e.target.value }))}>
                    {PAY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                {emp.pay_type !== 'volunteer' && (
                  <div className="form-group">
                    <label className="form-label">
                      Rate ($)
                      <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 4 }}>
                        {emp.pay_type === 'salary' ? '/ year' : emp.pay_type === 'hourly' ? '/ hr' : '/ athlete / mo'}
                      </span>
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      className="form-input"
                      value={emp.rate_dollars}
                      onChange={e => setEmp(p => ({ ...p, rate_dollars: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Pay Frequency</label>
                  <select className="form-input form-select" value={emp.payment_frequency} onChange={e => setEmp(p => ({ ...p, payment_frequency: e.target.value }))}>
                    {PAY_FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-input" value={emp.start_date} onChange={e => setEmp(p => ({ ...p, start_date: e.target.value }))} />
                </div>
              </div>

              {emp.rate_dollars && emp.pay_type !== 'volunteer' && (
                <div className="vdot-badge" style={{ alignSelf: 'flex-start' }}>
                  {centsToDisplay(Math.round(parseFloat(emp.rate_dollars) * 100), emp.pay_type)}
                  <span style={{ color: 'var(--gray-300)', margin: '0 4px' }}>·</span>
                  {PAY_FREQUENCIES.find(f => f.value === emp.payment_frequency)?.label} pay
                  <span style={{ color: 'var(--gray-300)', margin: '0 4px' }}>·</span>
                  {CONTRACT_TYPES.find(c => c.value === emp.contract_type)?.label}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Payment Notes</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={emp.payment_notes}
                  onChange={e => setEmp(p => ({ ...p, payment_notes: e.target.value }))}
                  placeholder="Direct deposit info, payment exceptions, etc."
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          </div>

          {/* Certifications & Background */}
          <div className="section-card" style={{ marginTop: 0 }}>
            <div className="section-card-header">
              <span className="section-card-title">Certifications & Background Check</span>
            </div>
            <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="form-group">
                <label className="form-label">Certifications</label>
                <input
                  type="text"
                  className="form-input"
                  value={emp.certifications}
                  onChange={e => setEmp(p => ({ ...p, certifications: e.target.value }))}
                  placeholder="e.g. USATF Level 1, SafeSport Certified, First Aid/CPR"
                />
              </div>
              <div className="plan-controls" style={{ flexWrap: 'wrap' }}>
                <div className="form-group">
                  <label className="form-label">Certification Expiry</label>
                  <input type="date" className="form-input" value={emp.cert_expiry} onChange={e => setEmp(p => ({ ...p, cert_expiry: e.target.value }))} />
                  {certExpired && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ Expired — renewal needed</p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Background Check Status</label>
                  <select className="form-input form-select" value={emp.background_check_status} onChange={e => setEmp(p => ({ ...p, background_check_status: e.target.value }))}>
                    {BG_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Background Check Date</label>
                  <input type="date" className="form-input" value={emp.background_check_date} onChange={e => setEmp(p => ({ ...p, background_check_date: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="section-card" style={{ marginTop: 0 }}>
            <div className="section-card-header">
              <span className="section-card-title">Emergency Contact</span>
            </div>
            <div style={{ padding: '24px 28px' }}>
              <div className="plan-controls" style={{ flexWrap: 'wrap' }}>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text" className="form-input"
                    value={emp.emergency_contact_name}
                    onChange={e => setEmp(p => ({ ...p, emergency_contact_name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel" className="form-input"
                    value={emp.emergency_contact_phone}
                    onChange={e => setEmp(p => ({ ...p, emergency_contact_phone: e.target.value }))}
                    placeholder="(555) 000-0000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* W-2 / Tax Documents */}
          <div className="section-card" style={{ marginTop: 0 }}>
            <div className="section-card-header">
              <span className="section-card-title">
                {emp.contract_type === '1099' ? '1099' : 'W-2'} Tax Document Log
              </span>
            </div>
            <div style={{ padding: '24px 28px' }}>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16, marginTop: 0 }}>
                Track which tax years have had documents issued. Record-keeping only.
              </p>
              <div className="plan-controls" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Tax Year</label>
                  <select className="form-input form-select" value={w2Year} onChange={e => setW2Year(Number(e.target.value))}>
                    {w2YearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: 'auto', alignSelf: 'flex-end' }}
                  onClick={handleIssueW2}
                  disabled={emp.w2_issued_years.includes(w2Year)}
                >
                  Mark {emp.contract_type === '1099' ? '1099' : 'W-2'} Issued for {w2Year}
                </button>
              </div>
              {emp.w2_issued_years.length > 0 ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {emp.w2_issued_years.map(y => (
                    <span key={y} className="admin-plan-badge admin-plan-badge--done" style={{ gap: 6 }}>
                      {y} {emp.contract_type === '1099' ? '1099' : 'W-2'} ✓
                      <button
                        onClick={() => handleRemoveW2Year(y)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 14, padding: '0 0 0 4px', lineHeight: 1 }}
                      >×</button>
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--gray-300)', margin: 0 }}>No tax documents logged yet.</p>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          <div className="section-card" style={{ marginTop: 0 }}>
            <div className="section-card-header">
              <span className="section-card-title">
                Admin Notes
                <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--gray-400)', marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>
                  private — not visible to coach
                </span>
              </span>
            </div>
            <div style={{ padding: '24px 28px' }}>
              <textarea
                className="form-input"
                rows={5}
                value={emp.admin_notes}
                onChange={e => setEmp(p => ({ ...p, admin_notes: e.target.value }))}
                placeholder="Performance notes, onboarding status, internal observations…"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Save */}
          <div style={{ paddingBottom: 40 }}>
            <button className="btn btn-primary" onClick={handleSaveEmployment} disabled={empSaving} style={{ width: 'auto' }}>
              {empSaving ? 'Saving…' : 'Save Employment Record'}
            </button>
          </div>

        </div>
      )}
    </Layout>
  );
}
